/**
 * Zawiadomienie do PSP o zakończeniu instalowania urządzeń fotowoltaicznych ponad 6,5 kW
 * (art. 29 ust. 4 pkt 3 lit. c Prawa budowlanego, tekst jednolity Dz.U. 2026 poz. 524).
 *
 * PO CO: komendy PSP publikują własne, różne wzory (pobrane 2026-10-09: Radom, Sanok, Wadowice, Łańcut,
 * Środa Śląska, Łódź; zestawienie w `VV/dane_badawcze/psp/README.md`). Jedno pismo z nadzbiorem ich pól
 * da się złożyć w każdej komendzie, bo forma zawiadomienia nie jest w przepisach określona.
 *
 * ZASADY (decyzje właściciela 2026-10-09):
 * - wstępnie wypełniamy, nie podpisujemy i nie składamy; czego nie wiemy, zostaje polem do wpisania;
 * - podstawą jest art. 29, nie uchylony art. 56 ust. 1a, na który powołuje się 5 z 6 wzorów komend;
 * - dane klienta zostają w przeglądarce (jak w zgłoszeniu do OSD); PESEL niepotrzebny, żaden wzór go nie ma;
 * - podpowiedzi dla domu jednorodzinnego (ZL IV, bez zagrożenia wybuchem) to wartości z przykładu wypełnienia
 *   KP PSP Wadowice, a nie nasza ocena; użytkownik może je zmienić.
 *
 * Czyste funkcje: `opisZawiadomienia` składa treść pisma, PDF rysuje `pdfZawiadomieniaPsp.js`.
 */
import { liczbaPl } from './zgloszenieOsd'

export const PODSTAWA_PRAWNA = 'art. 29 ust. 4 pkt 3 lit. c ustawy z dnia 7 lipca 1994 r. Prawo budowlane (Dz. U. z 2026 r. poz. 524)'
export const NORMA_OZNAKOWANIA = 'PN-HD 60364-7-712'

/** Rodzaj obiektu z podpowiedzią kwalifikacji pożarowej (pusta = do wpisania). */
export const OBIEKTY = [
  { klucz: 'dom', etykieta: 'Budynek mieszkalny jednorodzinny', kwalifikacja: 'ZL IV' },
  { klucz: 'wielorodzinny', etykieta: 'Budynek mieszkalny wielorodzinny', kwalifikacja: 'ZL IV' },
  { klucz: 'gospodarczy', etykieta: 'Budynek gospodarczy lub garaż wolnostojący', kwalifikacja: '' },
  { klucz: 'grunt', etykieta: 'Instalacja na gruncie (wolnostojąca)', kwalifikacja: 'nie dotyczy' },
  { klucz: 'inny', etykieta: 'Inny budynek', kwalifikacja: '' },
]

export const MONTAZ = [
  ['dach_skosny', 'na dachu skośnym'],
  ['dach_plaski', 'na dachu płaskim'],
  ['fasada', 'na fasadzie'],
  ['zintegrowany', 'zintegrowane z budynkiem (dachówki lub fasada PV)'],
  ['grunt', 'na konstrukcji gruntowej'],
]

export const MIEJSCA_FALOWNIKA = [
  ['garaz', 'w garażu'],
  ['kotlownia', 'w kotłowni lub pomieszczeniu technicznym'],
  ['sciana', 'na zewnętrznej ścianie budynku'],
  ['poddasze', 'na poddaszu'],
  ['piwnica', 'w piwnicy'],
  ['konstrukcja', 'przy konstrukcji gruntowej'],
  ['inne', 'w innym miejscu'],
]

/** Magazyn: zwykle obok falownika (falownik hybrydowy), ale bywa w innym pomieszczeniu. */
export const MIEJSCA_MAGAZYNU = [
  ['jak_falownik', 'w tym samym pomieszczeniu co falownik'],
  ...MIEJSCA_FALOWNIKA.filter(([k]) => k !== 'konstrukcja'),
]

/** Krótkie nazwy pomieszczeń do podpisu na planie dla ekip ratowniczych (ściana zewnętrzna i konstrukcja to nie pomieszczenia). */
export const NAZWY_POMIESZCZEN = {
  garaz: 'garaż', kotlownia: 'kotłownia', poddasze: 'poddasze', piwnica: 'piwnica', inne: 'pomieszczenie',
}

export const ROZLACZNIK_DC = [
  ['wbudowany', 'wbudowany w falownik'],
  ['przy_falowniku', 'osobny rozłącznik DC przy falowniku'],
  ['przy_modulach', 'rozłącznik DC przy modułach'],
  ['brak', 'brak rozłącznika DC'],
]

export const ODLACZANIE_MODULOW = [
  ['brak', 'brak (napięcie DC na przewodach od modułów do falownika zostaje)'],
  ['optymalizatory', 'optymalizatory mocy obniżające napięcie przy modułach'],
  ['mikroinwertery', 'mikroinwertery (po stronie dachu tylko pojedyncze moduły)'],
  ['szybkie', 'urządzenie do szybkiego odłączania przy modułach'],
]

export const MIEJSCA_OZNAKOWANIA = [
  ['zlacze', 'przy złączu lub liczniku energii'],
  ['rozdzielnica', 'przy rozdzielnicy głównej budynku'],
  ['pwp', 'przy przeciwpożarowym wyłączniku prądu'],
  ['falownik', 'przy falowniku'],
]

/** Pusty stan formularza z podpowiedziami dla domu jednorodzinnego. */
export const PUSTY_STAN = {
  // inwestor i adres: wyłącznie w przeglądarce
  inwestor: '', adresInwestora: '', telefon: '', miejscowosc: '',
  adresInstalacji: '', dzialka: '', komenda: '',
  pelnomocnik: false,
  // obiekt
  obiekt: 'dom', kwalifikacja: 'ZL IV', wysokosc: '', kondygnacje: '', podziemne: '0', powierzchnia: '', kubatura: '',
  osoby: '', zagrozenieWybuchem: 'nie',
  // instalacja
  montaz: 'dach_skosny', polozenieModulow: '', miejsceFalownika: 'garaz', miejsceFalownikaOpis: '',
  pomieszczenieMagazynu: 'jak_falownik', miejsceMagazynu: '', zasilanieAwaryjne: 'nie_wiem', agregat: 'nie',
  rozlacznikDc: 'wbudowany', rozlacznikDcOpis: '', odlaczanieModulow: 'brak',
  pwp: 'nie', pwpOpis: '', oznakowanie: ['zlacze', 'rozdzielnica'], odgromowa: 'nie', uszczelnienia: 'nie',
  wykonawca: '', dataMontazu: '', dataUzytkowania: '',
  // uzgodnienie
  rzeczoznawca: '', uprawnienia: '', dataUzgodnienia: '',
  // plan dla ekip ratowniczych (`planRatowniczy.js`): strony świata na rzucie, góra rysunku to północ
  planModuly: 'S', planFalownik: 'N', planRozdzielnica: 'N', planZlacze: 'N', planPwp: 'N', planWejscie: 'N',
  planMagazyn: 'N', planTrasaDc: 'wewnatrz', planOslony: 'nie', planAutor: '', planData: '',
}

const wybor = (lista, klucz) => (lista.find(([k]) => k === klucz) || [])[1] || ''
const pusty = (v) => v == null || String(v).trim() === ''
const kw = (v) => (v ? `${liczbaPl(v)} kW` : null)

/** Data ISO -> „dd.mm.rrrr”; pusta albo błędna -> ''. */
export const dataPl = (iso) => {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})$/)
  return m ? `${m[3]}.${m[2]}.${m[1]}` : ''
}

/** Opis urządzeń jednym zdaniem: „20 modułów JA Solar JAM54D40 (0,46 kW)”. */
const opisModulow = (m) => {
  if (!m?.liczba) return ''
  const nazwa = [m.producent, m.model].filter(Boolean).join(' ')
  return `${m.liczba} szt.${nazwa ? ` ${nazwa}` : ''}${m.mocKw ? ` po ${liczbaPl(m.mocKw * 1000, 0)} W` : ''}`
}
const opisFalownikow = (f) => {
  const nazwa = [f?.producent, f?.model].filter(Boolean).join(' ')
  if (!nazwa && !f?.mocKw) return ''
  return `${f.sztuk || 1} szt.${nazwa ? ` ${nazwa}` : ''}${f.mocKw ? `, ${kw(f.mocKw)}` : ''}${f.fazyZnane ? `, ${f.fazy === 1 ? 'jednofazowy' : 'trójfazowy'}` : ''}`
}
const opisMagazynu = (g) => {
  if (!g) return ''
  const nazwa = [g.producent, g.model].filter(Boolean).join(' ')
  return `${g.sztuk || 1} szt.${nazwa ? ` ${nazwa}` : ''}${g.kwhRazem ? `, razem ${liczbaPl(g.kwhRazem)} kWh` : ''}`
}

export const ZDANIE_DC_POD_NAPIECIEM = 'Przewody DC między modułami a falownikiem pozostają pod napięciem przy oświetlonych modułach, także po wyłączeniu zasilania budynku.'

/** Położenie falownika: „w garażu, parter”. */
export const polozenieFalownika = (s) => [wybor(MIEJSCA_FALOWNIKA, s.miejsceFalownika), (s.miejsceFalownikaOpis || '').trim()].filter(Boolean).join(', ')

/** Położenie magazynu: z wyboru i doprecyzowania; „jak falownik” podaje też miejsce falownika. */
export const polozenieMagazynu = (s) => {
  const opis = (s.miejsceMagazynu || '').trim()
  const gdzie = s.pomieszczenieMagazynu === 'jak_falownik'
    ? `w tym samym miejscu co falownik (${polozenieFalownika(s)})`
    : wybor(MIEJSCA_MAGAZYNU, s.pomieszczenieMagazynu)
  return [gdzie, opis].filter(Boolean).join(', ')
}

/** Zdanie o odłączaniu zasilania dla ekip ratowniczych (z wyborów, bez oceny wystarczalności). */
export const zdanieOdlaczenia = (s) => {
  const czesci = []
  czesci.push(s.pwp === 'tak'
    ? `Obiekt jest wyposażony w przeciwpożarowy wyłącznik prądu${pusty(s.pwpOpis) ? '' : ` (${s.pwpOpis.trim()})`}.`
    : 'Obiekt nie jest wyposażony w przeciwpożarowy wyłącznik prądu.')
  const dc = wybor(ROZLACZNIK_DC, s.rozlacznikDc)
  if (dc) czesci.push(`Odłączenie strony DC: ${dc}${pusty(s.rozlacznikDcOpis) ? '' : `, ${s.rozlacznikDcOpis.trim()}`}.`)
  if (s.odlaczanieModulow && s.odlaczanieModulow !== 'brak') czesci.push(`Przy modułach: ${wybor(ODLACZANIE_MODULOW, s.odlaczanieModulow)}.`)
  else czesci.push(ZDANIE_DC_POD_NAPIECIEM)
  return czesci.join(' ')
}

/** Zdanie o źródłach zasilania rezerwowego. */
export const zdanieZasilaniaRezerwowego = (sprzet, s) => {
  const zrodla = []
  if (sprzet?.magazyn) {
    if (s.zasilanieAwaryjne === 'tak') zrodla.push('magazyn energii z funkcją zasilania awaryjnego (zasila obwody budynku przy braku napięcia w sieci)')
    else if (s.zasilanieAwaryjne === 'nie') zrodla.push('magazyn energii bez funkcji zasilania awaryjnego')
    else zrodla.push('magazyn energii (do potwierdzenia, czy zasila budynek przy braku napięcia w sieci)')
  }
  if (s.agregat === 'tak') zrodla.push('agregat prądotwórczy')
  return zrodla.length ? `Występują: ${zrodla.join('; ')}.` : 'Nie występują.'
}

export const zdanieOznakowania = (s) => {
  const miejsca = (s.oznakowanie || []).map((k) => wybor(MIEJSCA_OZNAKOWANIA, k)).filter(Boolean)
  return miejsca.length
    ? `Wykonano zgodnie z ${NORMA_OZNAKOWANIA}: ${miejsca.join(', ')}.`
    : ''
}

/**
 * Treść pisma: nagłówek, sekcje z wierszami [etykieta, wartość] i lista załączników. Pusta wartość = pole do
 * wpisania w PDF. `dzis` tylko do testów.
 */
export const opisZawiadomienia = ({ sprzet, stan, dzis = new Date() }) => {
  const s = { ...PUSTY_STAN, ...stan }
  const obiekt = OBIEKTY.find((o) => o.klucz === s.obiekt) || OBIEKTY[0]
  const naGruncie = s.obiekt === 'grunt'
  const montaz = wybor(MONTAZ, s.montaz)
  const falownikGdzie = polozenieFalownika(s)
  const sumaKw = sprzet?.moduly?.sumaKw

  const sekcje = [
    { tytul: 'Lokalizacja', wiersze: [
      ['Rodzaj obiektu', obiekt.etykieta],
      ['Adres instalacji', s.adresInstalacji],
      ['Nr działki, obręb', s.dzialka],
    ] },
    { tytul: 'Dane instalacji fotowoltaicznej', wiersze: [
      ['Moc zainstalowana (moduły, DC)', sumaKw ? `${liczbaPl(sumaKw)} kWp` : ''],
      ['Moduły fotowoltaiczne', opisModulow(sprzet?.moduly)],
      ['Falownik', opisFalownikow(sprzet?.falownik)],
      ...(sprzet?.magazyn ? [['Magazyn energii', opisMagazynu(sprzet.magazyn)]] : []),
      ['Sposób montażu modułów', montaz],
      ['Współpraca z siecią', 'instalacja przyłączona do sieci (on-grid)'],
      ['Wykonawca instalacji', s.wykonawca],
      ['Data zakończenia montażu', dataPl(s.dataMontazu)],
      ['Planowane rozpoczęcie użytkowania', dataPl(s.dataUzytkowania)],
    ] },
    { tytul: 'Uzgodnienie projektu pod względem ochrony przeciwpożarowej', wiersze: [
      ['Rzeczoznawca do spraw zabezpieczeń przeciwpożarowych', s.rzeczoznawca],
      ['Numer uprawnień rzeczoznawcy', s.uprawnienia],
      ['Data uzgodnienia', dataPl(s.dataUzgodnienia)],
    ] },
    ...(naGruncie ? [] : [{ tytul: 'Dane obiektu', wiersze: [
      ['Kwalifikacja pożarowa (ZL I–V, PM, IN)', s.kwalifikacja],
      ['Wysokość obiektu [m]', s.wysokosc],
      ['Liczba kondygnacji (w tym podziemnych)', pusty(s.kondygnacje) ? '' : `${s.kondygnacje} (${pusty(s.podziemne) ? '0' : s.podziemne})`],
      ['Powierzchnia całkowita [m²]', s.powierzchnia],
      ['Kubatura [m³]', s.kubatura],
      ['Przewidywana liczba osób w obiekcie', s.osoby],
      ['Zagrożenie wybuchem', s.zagrozenieWybuchem === 'tak' ? 'występuje' : 'nie występuje'],
    ] }]),
    { tytul: 'Informacje dla ekip ratowniczych', wiersze: [
      ['Położenie modułów', s.polozenieModulow || (montaz ? `moduły ${montaz}` : '')],
      ['Położenie falownika', falownikGdzie],
      ...(sprzet?.magazyn ? [['Położenie magazynu energii', polozenieMagazynu(s)]] : []),
      ['Odłączenie zasilania', zdanieOdlaczenia(s)],
      ['Źródła zasilania rezerwowego', zdanieZasilaniaRezerwowego(sprzet, s)],
      ['Oznakowanie znakiem bezpieczeństwa', zdanieOznakowania(s)],
      ['Instalacja odgromowa', s.odgromowa === 'tak' ? 'występuje' : 'nie występuje'],
      ['Uszczelnienia ogniochronne przejść instalacyjnych', s.uszczelnienia === 'tak' ? 'występują' : 'nie występują'],
    ] },
  ]

  const zalaczniki = [
    'Plan urządzenia fotowoltaicznego dla ekip ratowniczych',
    'Kopia uzgodnienia projektu przez rzeczoznawcę do spraw zabezpieczeń przeciwpożarowych (z rzutem instalacji)',
    'Oświadczenie wykonawcy o wykonaniu instalacji zgodnie z uzgodnionym projektem i przepisami',
    'Schemat elektryczny instalacji',
    ...(s.pwp === 'tak' ? ['Protokół sprawdzenia działania przeciwpożarowego wyłącznika prądu'] : []),
    ...(s.odgromowa === 'tak' ? ['Protokół pomiarów instalacji odgromowej'] : []),
    ...(s.pelnomocnik ? ['Pełnomocnictwo'] : []),
  ]

  return {
    tytul: 'Zawiadomienie o zakończeniu instalowania urządzeń fotowoltaicznych',
    nadawca: [s.inwestor, s.adresInwestora, s.telefon ? `tel. ${s.telefon}` : ''],
    miejsceIData: [s.miejscowosc, dataPl(dzis.toISOString().slice(0, 10))].filter(Boolean).join(', '),
    adresat: s.komenda,
    tresc: `Na podstawie ${PODSTAWA_PRAWNA} zawiadamiam o zakończeniu instalowania urządzeń fotowoltaicznych `
      + `o mocy zainstalowanej elektrycznej większej niż 6,5 kW i rozpoczęciu ich użytkowania. `
      + 'Wraz z zawiadomieniem przekazuję plan urządzenia fotowoltaicznego dla ekip ratowniczych.',
    sekcje,
    zalaczniki,
    podpis: s.pelnomocnik ? 'podpis inwestora albo pełnomocnika' : 'podpis inwestora',
  }
}

/** Czego brakuje do złożenia (pola do wpisania w PDF albo w formularzu). */
export const doUzupelnieniaPsp = (opis) => {
  const braki = []
  if (!opis.nadawca[0]) braki.push('dane inwestora')
  if (!opis.adresat) braki.push('komenda PSP właściwa dla adresu instalacji')
  for (const sekcja of opis.sekcje) {
    for (const [etykieta, wartosc] of sekcja.wiersze) if (pusty(wartosc)) braki.push(etykieta.charAt(0).toLowerCase() + etykieta.slice(1))
  }
  return braki
}

/** Obowiązek dotyczy mocy modułów ponad 6,5 kWp. */
export { wymagaPpoz } from '../config/ppoz'
